import { FlakinessReport } from "@flakiness/flakiness-report";
import { Multimap } from "@flakiness/shared/common/multimap.js";
import { Brand, xxHashObject } from "@flakiness/shared/common/utils.js";
import { WireTypes } from "../wireTypes.js";
import { Stats } from "./stats.js";

type NormalizedValue = Brand<string, 'NormalizedValue'>;

function normalize(value: string): NormalizedValue {
  return value.toLowerCase().trim() as NormalizedValue;
}

export type EnvGroupType = WireTypes.JSONEnvGroup['type'];

type SystemType = 'linux'|'windows'|'macos';

export type EnvGroupDescription = {
  category: string,
  name?: string,
  configPath?: FlakinessReport.GitFilePath,
  // The same tokens as `metadata`, unjoined, for callers that lay them out themselves.
  metadataTokens: string[],
  metadata?: string,
  system?: string,
  systemType?: SystemType,
  // The runtime name and as much of its version as the group pins, e.g. `node 22`.
  runtime?: string,
};

/**
 * The JSON of the old `Timeline`. Links in GitHub PR comments, old
 * `test_timeline` URL params and web clients loaded before `EnvGroup` use it.
 */
export type LegacyJSONTimeline = {
  system: { name: string, value: string }[],
  user: { name: string, value: string }[],
};

/**
 * The JSON of the old `TimelineSplit`. Released CLIs, old `split` URL params
 * and web clients loaded before `EnvGroup` use it.
 */
export type LegacyJSONTimelineSplit = {
  splitByDefault?: boolean,
  inverse: {
    system?: string[],
    user?: string[],
  },
  filters: {
    system?: { name: string, values: string[] }[],
    user?: { name: string, values: string[] }[],
  },
};

/** A key of an environment, such as `os.name`. Keys are case-insensitive. */
export type EnvGroupKey = Brand<string, 'EnvGroupKey'>;

export namespace EnvGroupKey {
  export function from(key: string): EnvGroupKey {
    return key.toLowerCase() as EnvGroupKey;
  }

  export function metadata(name: string): EnvGroupKey {
    return from(`metadata.${name}`);
  }

  export const NAME = from('name');
  export const CATEGORY = from('category');
  export const CONFIG_PATH = from('config.path');
  export const OS_NAME = from('os.name');
  export const OS_VERSION = from('os.version');
  export const OS_ARCH = from('os.arch');
  // The runtime version is split into `major.minor.patch`, see `envEntries()`.
  export const RUNTIME_NAME = from('runtime.name');
  export const RUNTIME_MAJOR = from('runtime.major');
  export const RUNTIME_MINOR = from('runtime.minor');
  export const RUNTIME_PATCH = from('runtime.patch');
  export const RUNTIME_KEYS: readonly EnvGroupKey[] = [RUNTIME_NAME, RUNTIME_MAJOR, RUNTIME_MINOR, RUNTIME_PATCH];

  // Computed keys, see COMPUTED_KEYS.
  export const ENV_ID = from('env.id');
  export const OS_NAME_WITH_VERSION = from('os.name-version');
}

/**
 * Keys computed from other keys of the environment. They can be filtered by,
 * but they are not part of the environment's own keys, so projection and
 * closed matching ignore them.
 */
const COMPUTED_KEYS = new Map<EnvGroupKey, (env: WireTypes.RunEnvironment) => string|undefined>([
  // Pins a single environment. Links in GitHub PR comments use it.
  [EnvGroupKey.ENV_ID, env => env.envId],
  // OS name and version joined with a space. The CLI's `--env-os` sends it.
  [EnvGroupKey.OS_NAME_WITH_VERSION, env => [env.systemData.osName, env.systemData.osVersion].filter(Boolean).join(' ') || undefined],
]);

/**
 * The environment's own keys: `name`, `category`, `config.path`, `os.*`,
 * `runtime.*` and `metadata.*`. An empty value counts as absent.
 *
 * The runtime version splits on dots: the first token is the major, the
 * second is the minor, and the rest is the patch, e.g. `3.13.0rc1.dev2`
 * becomes `3`, `13` and `0rc1.dev2`.
 */
function envEntries(env: WireTypes.RunEnvironment): Map<EnvGroupKey, string> {
  const entries = new Map<EnvGroupKey, string>();
  const add = (key: EnvGroupKey, value: string|number|boolean|undefined|null) => {
    if (value !== undefined && value !== null && value !== '')
      entries.set(key, String(value));
  };
  add(EnvGroupKey.NAME, env.name);
  add(EnvGroupKey.CATEGORY, env.category);
  add(EnvGroupKey.CONFIG_PATH, env.configPath);
  add(EnvGroupKey.OS_NAME, env.systemData.osName);
  add(EnvGroupKey.OS_VERSION, env.systemData.osVersion);
  add(EnvGroupKey.OS_ARCH, env.systemData.osArch);
  if (env.runtime) {
    const [major, minor, ...patch] = env.runtime.version.split('.');
    add(EnvGroupKey.RUNTIME_NAME, env.runtime.name);
    add(EnvGroupKey.RUNTIME_MAJOR, major);
    add(EnvGroupKey.RUNTIME_MINOR, minor);
    add(EnvGroupKey.RUNTIME_PATCH, patch.join('.'));
  }
  for (const [name, value] of Object.entries(env.userSuppliedData ?? {}))
    add(EnvGroupKey.metadata(name), value);
  return entries;
}

// System keys of the old `Timeline` and `TimelineSplit`.
const LEGACY_SYSTEM_KEYS = new Map<string, EnvGroupKey>([
  ['env_id', EnvGroupKey.ENV_ID],
  ['name', EnvGroupKey.NAME],
  ['path', EnvGroupKey.CONFIG_PATH],
  ['category', EnvGroupKey.CATEGORY],
  ['osname', EnvGroupKey.OS_NAME_WITH_VERSION],
  ['os_name_no_version', EnvGroupKey.OS_NAME],
  ['osarch', EnvGroupKey.OS_ARCH],
]);

/**
 * Both legacy shapes become open groups, since that is how they were
 * matched. Split settings of old splits are ignored: every environment
 * becomes its own timeline, as with the old default split.
 */
function fromLegacyJSON(json: LegacyJSONTimeline|LegacyJSONTimelineSplit) {
  const eq: Record<EnvGroupKey, string[]> = {};
  const add = (name: string, values: string[], isUserData: boolean) => {
    const key = isUserData ? EnvGroupKey.metadata(name) : LEGACY_SYSTEM_KEYS.get(name);
    if (key)
      eq[key] = [...(eq[key] ?? []), ...values];
  };
  if ('inverse' in json) {
    for (const { name, values } of json.filters.system ?? [])
      add(name, values, false);
    for (const { name, values } of json.filters.user ?? [])
      add(name, values, true);
  } else {
    for (const { name, value } of json.system)
      add(name, [value], false);
    for (const { name, value } of json.user)
      add(name, [value], true);
  }
  return EnvGroup.open(eq);
}

// Keys and values are sorted. A key with a single value serializes as a string.
function jsonFilters<V extends string>(filters: Multimap<EnvGroupKey, V>): Record<string, V|V[]> {
  const json: Record<string, V|V[]> = {};
  for (const key of [...filters.keys()].sort()) {
    const values = filters.getAll(key).sort();
    json[key] = values.length === 1 ? values[0] : values;
  }
  return json;
}

// The environment's own keys, except the dropped ones, each with its value.
function envFilters(env: WireTypes.RunEnvironment, droplist: ReadonlySet<EnvGroupKey>): Record<EnvGroupKey, string|string[]> {
  const eq: Record<EnvGroupKey, string|string[]> = {};
  for (const [key, value] of envEntries(env)) {
    if (!droplist.has(key))
      eq[key] = value;
  }
  return eq;
}

/**
 * A set of environments.
 *
 * Filters map a key to the values it may take; an environment must match
 * every filtered key, and any of its values. Keys and values match
 * case-insensitively.
 *
 * - An `open` filter ignores the keys it doesn't list.
 * - A `closed` filter also requires that the environment has no keys besides
 *   the listed and the dropped ones.
 *
 * The droplist names keys that {@link subgroups} removes before grouping
 * environments.
 */
export class EnvGroup {
  /**
   * The maximum number of distinct timelines we expect to have per query.
   * This constant is used throughout the engine to establish per-timeline caches.
   */
  static MAX_TIMELINES = 500;

  static categoryCollectionLabel(category: string) {
    if (category === FlakinessReport.CATEGORY_PLAYWRIGHT)
      return 'Playwright Environments';
    if (category === FlakinessReport.CATEGORY_JUNIT)
      return 'JUnit Environments';
    if (category === FlakinessReport.CATEGORY_PYTEST)
      return 'Pytest Environments';
    if (category === 'perf')
      return 'Performance Benchmarks';
    return `${category} environments`;
  }

  static osLabel(input: SystemDataLike): string {
    return osLabel(input);
  }

  static open(eq: Record<EnvGroupKey, string|string[]> = {}, droplist: Iterable<EnvGroupKey> = []) {
    return new EnvGroup('open', eq, droplist);
  }

  static closed(eq: Record<EnvGroupKey, string|string[]> = {}, droplist: Iterable<EnvGroupKey> = []) {
    return new EnvGroup('closed', eq, droplist);
  }

  static closedFromEnv(env: WireTypes.RunEnvironment, droplist: ReadonlySet<EnvGroupKey> = new Set()) {
    return EnvGroup.closed(envFilters(env, droplist), droplist);
  }

  /** Accepts the legacy JSON as well. */
  static deserialize(json: WireTypes.JSONEnvGroup|LegacyJSONTimeline|LegacyJSONTimelineSplit) {
    if (!('type' in json))
      return fromLegacyJSON(json);
    const group = new EnvGroup(json.type, {}, (json.drop ?? []).map(EnvGroupKey.from));
    // Keys that differ only in case are the same key, so their values add up.
    for (const [key, values] of Object.entries(json.eq ?? {}))
      group._addEq({ [EnvGroupKey.from(key)]: values });
    return group;
  }

  // Normalized values are used for matching and identity.
  private _filters = new Multimap<EnvGroupKey, NormalizedValue>();
  // The same values as they were given, one spelling per normalized value, for display.
  private _rawFilters = new Multimap<EnvGroupKey, string>();
  private _droplist = new Set<EnvGroupKey>();

  private _memoizedAccepts = new Map<Stats.EnvId, boolean>();
  private _memoizedSubgroups = new Map<Stats.EnvId, EnvGroup|null>();
  private _memoizedEtag?: string;
  private _memoizedDescription?: EnvGroupDescription;

  private constructor(private readonly _type: EnvGroupType, eq: Record<EnvGroupKey, string|string[]> = {}, droplist: Iterable<EnvGroupKey> = []) {
    this._addEq(eq);
    this._drop(droplist);
  }

  /**
   * A copy of this group with the values added to the filters of their keys.
   * An environment matches a key when it has any of the key's values.
   */
  addEq(eq: Record<EnvGroupKey, string|string[]>): EnvGroup {
    const group = this._copy();
    group._addEq(eq);
    return group;
  }

  /** A copy of this group with the keys added to its droplist. */
  drop(...keys: EnvGroupKey[]): EnvGroup {
    const group = this._copy();
    group._drop(keys);
    return group;
  }

  private _addEq(eq: Record<EnvGroupKey, string|string[]>) {
    for (const [key, rawValues] of Object.entries(eq) as [EnvGroupKey, string|string[]][]) {
      for (const value of typeof rawValues === 'string' ? [rawValues] : rawValues) {
        const normalized = normalize(value);
        if (this._filters.has(key, normalized))
          continue;
        this._filters.set(key, normalized);
        this._rawFilters.set(key, value);
      }
    }
  }

  private _drop(keys: Iterable<EnvGroupKey>) {
    for (const key of keys)
      this._droplist.add(key);
  }

  private _copy(): EnvGroup {
    const group = new EnvGroup(this._type);
    group._filters = this._filters.clone();
    group._rawFilters = this._rawFilters.clone();
    group._droplist = new Set(this._droplist);
    return group;
  }

  groupType(): EnvGroupType {
    return this._type;
  }

  /** Whether the group accepts every environment. The droplist doesn't matter: it only affects {@link subgroups}. */
  acceptsAll(): boolean {
    return this._type === 'open' && !this._filters.size;
  }

  /** The keys the group filters by. A subgroup filters by its environments' keys, apart from the dropped ones. */
  keys(): EnvGroupKey[] {
    return [...this._filters.keys()];
  }

  values(key: EnvGroupKey): string[] {
    return this._rawFilters.getAll(key);
  }

  droplist(): ReadonlySet<EnvGroupKey> {
    return this._droplist;
  }

  private _computeAccepts(env: WireTypes.RunEnvironment): boolean {
    const entries = envEntries(env);
    for (const key of this._filters.keys()) {
      const extract = COMPUTED_KEYS.get(key);
      const value = extract ? extract(env) : entries.get(key);
      if (value === undefined || !this._filters.has(key, normalize(value)))
        return false;
    }
    if (this._type === 'open')
      return true;
    return [...entries.keys()].every(key => this._filters.hasAny(key) || this._droplist.has(key));
  }

  accepts(env: WireTypes.RunEnvironment): boolean {
    let result = this._memoizedAccepts.get(env.envId);
    if (result === undefined) {
      result = this._computeAccepts(env);
      this._memoizedAccepts.set(env.envId, result);
    }
    return result;
  }

  /**
   * Splits the environments of `envs` that this group accepts. Two
   * environments share a subgroup when their keys and values match after
   * the dropped keys are removed.
   *
   * A subgroup is closed: it pins the remaining keys and keeps the droplist.
   * It also keeps this group's filters on the keys it doesn't pin, so it
   * accepts only environments that this group accepts.
   */
  subgroups(envs: WireTypes.RunEnvironment[]): EnvGroup[] {
    const subgroups = new Map<string, EnvGroup>();
    for (const env of envs) {
      const subgroup = this.subgroup(env);
      if (subgroup && !subgroups.has(subgroup.etag()))
        subgroups.set(subgroup.etag(), subgroup);
    }
    return [...subgroups.values()];
  }

  /** The subgroup of {@link subgroups} that the environment lands in, or undefined if this group doesn't accept it. */
  subgroup(env: WireTypes.RunEnvironment): EnvGroup|undefined {
    let subgroup = this._memoizedSubgroups.get(env.envId);
    if (subgroup === undefined) {
      subgroup = this._computeSubgroup(env);
      this._memoizedSubgroups.set(env.envId, subgroup);
    }
    return subgroup ?? undefined;
  }

  private _computeSubgroup(env: WireTypes.RunEnvironment): EnvGroup|null {
    if (!this.accepts(env))
      return null;
    const eq = envFilters(env, this._droplist);
    for (const key of this._rawFilters.keys()) {
      if (!Object.hasOwn(eq, key))
        eq[key] = this._rawFilters.getAll(key);
    }
    return EnvGroup.closed(eq, this._droplist);
  }

  /** Keys with several values are left out of the description. */
  description(): EnvGroupDescription {
    if (!this._memoizedDescription)
      this._memoizedDescription = this._computeDescription();
    return this._memoizedDescription;
  }

  compare(other: EnvGroup) {
    const a = this.description();
    const b = other.description();
    return cmpText(a.name ?? a.configPath, b.name ?? b.configPath)
      ?? cmpText(a.metadata, b.metadata)
      ?? cmpText(a.system, b.system)
      ?? cmpText(a.runtime, b.runtime)
      ?? cmpText(a.configPath, b.configPath)
      ?? cmpText(a.category, b.category)
      ?? 0;
  }

  serialize(): WireTypes.JSONEnvGroup {
    return {
      type: this._type,
      eq: this._rawFilters.size ? jsonFilters(this._rawFilters) : undefined,
      drop: this._droplist.size ? [...this._droplist].sort() : undefined,
    };
  }

  etag() {
    if (!this._memoizedEtag) {
      this._memoizedEtag = xxHashObject({
        type: this._type,
        filters: jsonFilters(this._filters),
        droplist: [...this._droplist].sort(),
      });
    }
    return this._memoizedEtag;
  }

  isEqual(other: EnvGroup) {
    return this.etag() === other.etag();
  }

  /** Whether both filters accept the same environments out of `envs`. */
  isFunctionallyEqual(other: EnvGroup, envs: WireTypes.RunEnvironment[]): boolean {
    return envs.every(env => this.accepts(env) === other.accepts(env));
  }

  private _computeDescription(): EnvGroupDescription {
    const single = (key: EnvGroupKey) => {
      const values = this.values(key);
      return values.length === 1 ? values[0] : undefined;
    };
    const name = single(EnvGroupKey.NAME);
    const systemData = {
      osName: single(EnvGroupKey.OS_NAME) ?? single(EnvGroupKey.OS_NAME_WITH_VERSION),
      osVersion: single(EnvGroupKey.OS_VERSION),
      osArch: single(EnvGroupKey.OS_ARCH),
    };

    const tokens: string[] = [];
    for (const key of this._filters.keys()) {
      const value = key.startsWith('metadata.') ? single(key) : undefined;
      if (value === undefined || value.toLowerCase() === 'false' || value.toLowerCase() === 'default')
        continue;
      const metadataKey = key.slice('metadata.'.length);
      if (value.toLowerCase() === 'true')
        tokens.push(metadataKey);
      else if (Number.isFinite(Number(value)))
        tokens.push(`${metadataKey}=${value}`);
      else
        tokens.push(value);
    }
    const system = osLabel(systemData);
    const systemType = osType(systemData);

    // A version part counts only when the parts before it are pinned too.
    const runtimeVersion: string[] = [];
    for (const key of [EnvGroupKey.RUNTIME_MAJOR, EnvGroupKey.RUNTIME_MINOR, EnvGroupKey.RUNTIME_PATCH]) {
      const value = single(key);
      if (value === undefined)
        break;
      runtimeVersion.push(value);
    }
    const runtimeName = single(EnvGroupKey.RUNTIME_NAME);
    const runtime = runtimeName ? [runtimeName, runtimeVersion.join('.')].filter(Boolean).join(' ') : undefined;

    const impliedContext: string[] = [...tokens, system];
    if (name)
      impliedContext.push(name);
    if (systemType === 'macos')
      impliedContext.push('mac', 'macos', 'mac os');
    else if (systemType === 'windows')
      impliedContext.push('win32', 'win', 'windows', 'mingw');
    else if (systemType === 'linux')
      impliedContext.push('linux');
    if (tokens.some(token => token.startsWith('chrome')))
      impliedContext.push('chromium');

    // Drop tokens that are substrings of other tokens, the name, or the OS.
    const metadataTokens = tokens.filter((theToken, theIndex) => impliedContext.every((aToken, aIndex) => aIndex === theIndex || aToken.toLowerCase().indexOf(theToken.toLowerCase()) === -1));
    return {
      name,
      category: single(EnvGroupKey.CATEGORY) ?? '',
      configPath: single(EnvGroupKey.CONFIG_PATH) as FlakinessReport.GitFilePath|undefined,
      metadataTokens,
      metadata: metadataTokens.length ? metadataTokens.join(', ') : undefined,
      system,
      systemType,
      runtime,
    };
  }
}

type SystemDataLike = {
  osName?: string|null,
  osVersion?: string|null,
  osArch?: string|null,
};

/**
 * Ingestion stores `unknown` for system data fields that the report
 * leaves out, so labels treat it as absent.
 */
function knownValue(value: string|null|undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed.toLowerCase() === 'unknown' ? '' : trimmed;
}

function osType(input: SystemDataLike): SystemType|undefined {
  const lower = (input.osName ?? '').trim().toLowerCase();
  if (!lower)
    return undefined;
  if (/^(darwin|mac ?os|macos)\b/.test(lower))
    return 'macos';
  if (/^(mingw|win32|windows)/.test(lower))
    return 'windows';
  if (/^(ubuntu|linux)\b/.test(lower))
    return 'linux';
}

function osNameLabel(input: SystemDataLike): string {
  const rawName = knownValue(input.osName);
  const rawVersion = knownValue(input.osVersion);
  const suffix = (value: string, prefix: RegExp) => value.replace(prefix, '').trim();

  let name: string;
  if (/^(darwin|mac ?os|macos)\b/i.test(rawName))
    name = ['macOS', suffix(rawName, /^(darwin|mac ?os|macos)\b\s*/i)].filter(Boolean).join(' ');
  else if (/^mingw/i.test(rawName))
    name = ['Windows', suffix(rawName, /^mingw(?:32|64)?[_\s-]*/i)].filter(Boolean).join(' ');
  else if (/^(win32|windows)\b/i.test(rawName))
    name = ['Windows', suffix(rawName, /^(win32|windows)\b\s*/i)].filter(Boolean).join(' ');
  else if (/^ubuntu\b/i.test(rawName))
    name = ['Ubuntu', suffix(rawName, /^ubuntu\b\s*/i)].filter(Boolean).join(' ');
  else if (/^linux\b/i.test(rawName))
    name = ['Linux', suffix(rawName, /^linux\b\s*/i)].filter(Boolean).join(' ');
  else
    name = rawName;

  if (rawVersion && !name.toLowerCase().includes(rawVersion.toLowerCase()))
    name = [name, rawVersion].filter(Boolean).join(' ');
  return name.trim();
}

function osArchLabel(input: SystemDataLike): string {
  const rawArch = knownValue(input.osArch);
  const lower = rawArch.toLowerCase();
  if (!lower)
    return '';
  if (['x86_64', 'amd64', 'x64'].includes(lower))
    return 'x86-64';
  if (['aarch64', 'arm64'].includes(lower))
    return 'arm64';
  if (['x86', 'i386', 'i686'].includes(lower))
    return 'x86';
  return rawArch;
}

function osLabel(input: SystemDataLike): string {
  const arch = osArchLabel(input);
  const archLabel = arch && arch !== 'x86' && arch !== 'x86-64' ? arch : '';
  return [osNameLabel(input), archLabel].filter(Boolean).join(' ');
}

function cmpText(a: string = '', b: string = '') {
  a = a.toLowerCase();
  b = b.toLowerCase();
  if (a === b)
    return undefined;
  return a < b ? -1 : 1;
}
